<template>
  <div class="dashboard-container">
    <div v-if="loading" class="progress-bar" :style="{ width: progress + '%' }"></div>
    <div class="container">
      <div class="mb-2 page-title">
        <i class="bi bi-file-earmark-text"></i>
        {{ isEdit ? "Ubah Bagan" : "Buat Bagan" }}
      </div>

      <form @submit.prevent="submitForm" class="admin-form mt-4">
        <div class="row">
          <div class="col-lg-12">
            <div class="mb-3">
              <label for="tournament_id" class="form-label">Turnamen</label>
              <select class="form-select" id="tournament_id" v-model="form.tournament_id">
                <option value="" disabled>Pilih Turnamen</option>
                <option v-for="tournament in activeTournaments" :key="tournament.id" :value="tournament.id">
                  {{ tournament.name }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label for="match_category_id" class="form-label">Kategori Pertandingan</label>
              <select class="form-select" id="match_category_id" v-model="form.match_category_id">
                <option value="" disabled>Pilih Kategori Pertandingan</option>
                <option v-for="category in matchCategories" :key="category.id" :value="category.id">
                  {{ category.name }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label for="age_category_id" class="form-label">Kelompok Usia</label>
              <select class="form-select" id="age_category_id" v-model="form.age_category_id">
                <option value="" disabled>Pilih Kelompok Usia</option>
                <option v-for="ageCategory in ageCategories" :key="ageCategory.id" :value="ageCategory.id">
                  {{ ageCategory.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12 text-center">
            <button type="submit" class="button button-primary" :disabled="loading">
              <i class="bi bi-floppy"></i>
              <span>{{ isEdit ? "Simpan Bagan" : "Tampilkan Bagan" }}</span>
            </button>
          </div>
        </div>
      </form>

      <!-- Tournament Bracket -->
      <div class="row">
        <div class="bracket-container">
          <Bracket v-if="bracketMatches.length > 0" :flat-tree="bracketMatches">
            <template v-slot:player="{ player }">
              {{ player.name }}
            </template>
          </Bracket>
          <p v-else>Belum ada bracket yang tersedia.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification";
import Bracket from "vue-tournament-bracket"; // Importing the bracket library

export default {
  name: "MatchChartForm",
  components: {
    Bracket
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      form: {
        tournament_id: "",
        match_category_id: "",
        age_category_id: "",
      },
      activeTournaments: [],
      matchCategories: [],
      ageCategories: [],
      errors: {},
      loading: false,
      bracketMatches: [], // Store the bracket data
    };
  },

  created() {
    this.fetchActiveTournaments();
    this.fetchMatchCategories();
    this.fetchAgeCategories();
  },

  watch: {
    'form.tournament_id'() {
      this.muatKategoriEvent();
    },
  },

  methods: {
    /** Hanya kategori yang dibuka di event terpilih. */
    async muatKategoriEvent() {
      if (!this.form.tournament_id) {
        this.matchCategories = [];
        return;
      }
      try {
        const { data } = await axios.get('/fetch-match-categories', {
          params: { tournament_id: this.form.tournament_id, untuk: 'drawing' },
        });
        this.matchCategories = Array.isArray(data) ? data : data?.data || [];
      } catch (e) {
        this.matchCategories = [];
      }
    },

    formatBracket(bracketData) {
      let matchMap = {}; // Simpan referensi pertandingan berdasarkan ID

      // Simpan pertandingan ke dalam matchMap
      bracketData.forEach((roundData) => {
        roundData.matches.forEach((match) => {
          matchMap[match.id] = match;
        });
      });


      // Format bracket dengan `next` yang benar
      return bracketData.flatMap((roundData) => 
        roundData.matches.map((match) => {
          let nextMatch = matchMap[match.next] || null;

          return {
            id: match.id,
            next: nextMatch ? nextMatch.id : null,
            player1: {
              id: `m${match.id}p1`,
              name: match.player1?.name || "TBD",
              winner: match.winner_id ? match.winner_id === match.player1?.id : null,
            },
            player2: {
              id: `m${match.id}p2`,
              name: match.player2?.name || "TBD",
              winner: match.winner_id ? match.winner_id === match.player2?.id : null,
            },
          };
        })
      );
    },

    async submitForm() {
      this.loading = true;
      try {
        const response = await axios.get(
          `/show-bracket/${this.form.tournament_id}/${this.form.match_category_id}/${this.form.age_category_id}`
        );


        if (response.data && Array.isArray(response.data.bracket)) {
          this.bracketMatches = this.formatBracket(response.data.bracket);
        } else {
          console.error("Invalid bracket format:", response.data);
          this.bracketMatches = [];
        }
      } catch (error) {
        console.error("Error fetching bracket:", error);
        this.bracketMatches = [];
      } finally {
        this.loading = false;
      }
    },

    async fetchActiveTournaments() {
      await this.fetchData("/tournaments/active", "activeTournaments");
    },
    async fetchMatchCategories() {
      await this.muatKategoriEvent();
    },
    async fetchAgeCategories() {
      await this.fetchData("/age-categories", "ageCategories");
    },

    async fetchData(url, targetKey) {
      try {
        const response = await axios.get(url);
        this[targetKey] = response.data;
      } catch (error) {
        this.toast.error("Gagal memuat pilihan. Muat ulang halaman.");
      }
    },
  },

  setup() {
    const toast = useToast();
    return { toast };
  },
};
</script>


<style scoped>

.progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 5px;
  background: var(--grad-flame);
}

.bracket-container {
  overflow: auto;
  width: 100%;
 
}

::v-deep(.vtb-item-players) {
  width:200px;
  background-color: var(--bg-sunken);
}

::v-deep(.vtb-player1) {
  font-size: 12px;
  color: var(--text-primary);
  width:100%;
  height: 50px;
  padding:12px;
  border-left: 5px solid var(--success-500);
  border-top: 1px solid var(--border-subtle);
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  
  border-right: 1px solid var(--border-subtle);
  background: var(--bg-surface);
}

::v-deep(.vtb-player1)::after {
  width: 100%;
  height: 1px;
  background: var(--grad-flame);
  display: block;
  content: "";
  position: absolute;
  top: 50%;
  left: 0px;
}

::v-deep(.vtb-player2) {
  font-size: 12px;
  color: var(--text-primary);
  width:100%;
  height: 50px;
  padding:12px;
  border-left: 5px solid var(--border-strong);
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  border-bottom: 1px solid var(--border-subtle);
  border-right: 1px solid var(--border-subtle);
  background: var(--bg-surface);

}

::v-deep(.vtb-item-child:after, .vtb-item-child:before) {
    content: "";
    position: absolute;
    background: var(--grad-flame);
    top: 50%;
}

::v-deep(.vtb-item-child:before) {
    right: 0;
    -webkit-transform: translateX(100%);
    transform: translateX(100%);
    width: 25px;
    height: 1px;
}

::v-deep(.vtb-item-parent:after) {
    position: absolute;
    content: "";
    width: 25px;
    height: 1px;
    left: 0;
    top: 50%;
    background: var(--grad-flame);
    transform: translateX(-100%);
}

::v-deep(.vtb-item-child:after){
    right: -25px;
    height: calc(50% + 22px);
    width: 1px;
}
</style>
